<template>
  <Card
    v-if="show"
    :footerDivider="false"
    :loadingState="loading"
    :title="$t('idLevelBoxTitle')"
    iconClass="icon-vertrag_Level1"
  >
    <template v-slot:content>
      <div class="container mb-32">
        <img
          alt="IdLevelBoxIcon"
          :src="
            serviceCodeRequested
              ? assetsBaseUrl + '/assets/images/IdLevelBox/IdLevelBoxIconRequested.svg'
              : assetsBaseUrl + '/assets/images/IdLevelBox/IdLevelBoxIconRequired.svg'
          "
        />
        <span class="h2 label">
          {{
            serviceCodeRequested
              ? $t('serviceCodeRequested.text1')
              : $t('serviceCodeRequired.text1')
          }}
        </span>
      </div>
      <div>
        <p
          v-if="serviceCodeRequested"
          v-dompurify-html="$t('serviceCodeRequested.text2', { pukLetterCreated })"
        ></p>
        <p
          v-if="!serviceCodeRequested"
          v-dompurify-html="$t('serviceCodeRequired.text2')"
        ></p>
        <p>
          {{
            serviceCodeRequested
              ? $t('serviceCodeRequested.text3')
              : $t('serviceCodeRequired.text3')
          }}
        </p>
      </div>
    </template>
    <template v-slot:footer>
      <div class="footer-cta footer-cta--left">
        <CustomButton
          @click="onClickButton"
          :button-color="ButtonColor.PRIMARY"
          :button-type="ButtonType.BUTTON"
          :buttonId="
            serviceCodeRequested ? 'gotoEnterServicecode' : 'gotoRequestServicecode'
          "
          >{{
            serviceCodeRequested
              ? $t('serviceCodeRequested.button')
              : $t('serviceCodeRequired.button')
          }}
        </CustomButton>
        <CustomButton
          @click="onClickLabel"
          :buttonColor="ButtonColor.LINK"
          :buttonId="serviceCodeRequested ? 'gotoSMS' : 'gotoEnterServicecode'"
          :buttonType="ButtonType.BUTTON"
          >{{
            serviceCodeRequested
              ? $t('serviceCodeRequested.label')
              : $t('serviceCodeRequired.label')
          }}
        </CustomButton>
      </div>
    </template>
  </Card>
</template>

<script lang="ts" setup>
import { ButtonColor, ButtonType, Card, CustomButton } from 'de-zurich-ui-library'
import { navigate } from '@/utils/NavigationUtils'
import { getIdLevel, getPartnerProxyId, getToken } from '@/utils/KeycloakUtils'
import { fetchAccountsByPPID } from '@/stores/accountManagementService'
import dayjs from 'dayjs'
import { onMounted, ref } from 'vue'
import { ResponseError } from 'account-api'

const loading = ref(false)
const show = ref(false)
const lockedContracts = ref(false)
const serviceCodeRequested = ref(false)
const pukLetterCreated = ref('')

onMounted(async () => {
  const idLevel: number | null = getIdLevel()
  switch (idLevel) {
    case 1:
      break
    case 0:
      await onLoad()
      break
    default:
      console.error('No id level available!')
  }
})

const assetsBaseUrl = globalThis.ENV_CONFIG?.ASSETS_BASE_URL.CUSTOMER_PORTAL || ''

const onClickButton = (): void => {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  serviceCodeRequested.value
    ? navigate(
        globalThis.ENV_CONFIG?.SERVICE_LINKS.SETTINGS_PAGE.CHANGE_SERVICECODEINPUT_PAGE
      )
    : navigate(
        globalThis.ENV_CONFIG?.SERVICE_LINKS.SETTINGS_PAGE.CHANGE_SERVICECODEREQUEST_PAGE
      )
}

const onClickLabel = (): void => {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  serviceCodeRequested.value
    ? navigate(
        globalThis.ENV_CONFIG?.SERVICE_LINKS.SETTINGS_PAGE.CHANGE_SERVICECODEREQUEST_PAGE
      )
    : navigate(
        globalThis.ENV_CONFIG?.SERVICE_LINKS.SETTINGS_PAGE.CHANGE_SERVICECODEINPUT_PAGE
      )
}

function retrievePartnerProxyId(): string {
  const partnerProxyId = getPartnerProxyId()
  if (!partnerProxyId) {
    console.error('No partner proxy id available!')
  }
  return partnerProxyId || ''
}

const onLoad = async (): Promise<void> => {
  return new Promise((resolve: () => void) => {
    loading.value = true
    const partnerProxyId = retrievePartnerProxyId()
    fetchContracts(partnerProxyId)
      .then(async (res) => {
        if (res && res.count > 0) {
          await fetchAccountsIfNeeded(partnerProxyId)(res)
          show.value = true
        }
      })
      .finally(() => {
        loading.value = false
        resolve()
      })
      .catch(() => resolve())
  })
}

const fetchContracts = async (partnerProxyId: string): Promise<{ count: number }> => {
  try {
    const token = await getToken()
    const response = await fetch(
      `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.POLICIES}/contracts/locked?partnerProxyId=${partnerProxyId}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          KeyId: globalThis.ENV_CONFIG?.API_KEY,
          'Content-Type': 'application/json'
        }
      }
    )
    if (!response.ok) return { count: 0 }
    return response.json()
  } catch {
    return { count: 0 }
  }
}

const fetchAccountsIfNeeded =
  (partnerProxyId: string) => async (res: { count: number }) => {
    if (res.count === 0) return
    lockedContracts.value = true
    try {
      const accounts = await fetchAccountsByPPID(partnerProxyId)
      if (accounts && accounts[0]?.pukLetterCreated) {
        pukLetterCreated.value = dayjs(accounts[0]?.pukLetterCreated).format('DD.MM.YYYY')
        serviceCodeRequested.value = true
      }
    } catch (e) {
      if (e instanceof ResponseError) {
        console.error('Failed to fetch accounts:', e.response.status)
      } else {
        console.error('Failed to fetch accounts:', e)
      }
    }
  }
</script>

<style lang="scss" scoped>
.container {
  align-items: center;
  display: flex;
}

.label {
  hyphens: none !important;
}

.container img {
  margin-bottom: 0 !important;
  margin-right: 24px;
  width: 90px !important;
}

@media only screen and (max-width: 599.98px) {
  .label {
    line-height: 22px;
  }
}

@media only screen and (min-width: 600px) {
  .card__content--xs .label {
    font-size: var(--base-font-size);
    line-height: 22px;
  }
}

@media only screen and (min-width: 600px) {
  .container img {
    margin-right: 48px;
    width: 130px !important;
  }
}
</style>
